import { VueCharts, mixins } from 'vue-chartjs';
import ChartJsPluginDataLabels from 'chartjs-plugin-datalabels';
import { getDimension } from '../services/analysis';

const { reactiveProp } = mixins;

const baseColor = {
  positive: [64, 129, 207],
  negative: [255, 0, 0]
};
const steps = {
  positive: [170, 113, 42],
  negative: [0, 225, 225]
};

export default {
  extends: VueCharts.HorizontalBar,
  mixins: [reactiveProp],
  props: ['chartData', 'type', 'programmeName', 'skipTotals'],
  components: {
    ChartJsPluginDataLabels
  },
  watch: {
    chartData: {
      handler(newData) {
        if (newData) this.draw();
      }
    },
    skipTotals: {
      handler(newData) {
        this.draw();
      }
    }
  },
  mounted() {
    this.addPlugin({
      beforeDraw(chartInstance) {
        const ctx = chartInstance.chart.ctx; // eslint-disable-line prefer-destructuring
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, chartInstance.chart.width, chartInstance.chart.height);
      }
    });
    if (this.chartData) this.draw();
  },
  methods: {
    draw() {
      // clear out the old ones
      const datasetLookup = {};
      let datasetIndex = 0;
      const barchartData = {
        labels: [],
        datasets: []
      };
      const totals = [];
      let doTotals = false;
      const positive = this.type === 'positive';
      const color = baseColor[this.type === 'positive' ? 'positive' : 'negative'];
      const colorStep = steps[positive ? 'positive' : 'negative'];

      JSON.parse(JSON.stringify(this.chartData)).filter((scoreRow) => {
        if (positive && !scoreRow.scoreBackwards) return true;
        if (!positive && scoreRow.scoreBackwards) return true;
        return false;
      }).forEach((scoreRow) => {
        // make sure the label exists
        if (barchartData.labels.indexOf(scoreRow.elementName) === -1) barchartData.labels.push(scoreRow.elementName);
        const labelIndex = barchartData.labels.indexOf(scoreRow.elementName);
        if (totals.length < labelIndex + 1) {
          totals.push({
            dimensions: 0,
            responses: 0,
            cumulativeScore: 0
          });
        }
        const dimension = getDimension(scoreRow);
        let dataIndex = datasetLookup[dimension];
        if (!dataIndex && dataIndex !== 0) {
          // get our IDs all in a row
          datasetLookup[dimension] = datasetIndex;
          dataIndex = datasetLookup[dimension];
          datasetIndex += 1;

          barchartData.datasets.push({
            label: dimension,
            data: [],
            datalabels: {
              display: true,
              anchor: positive ? 'end' : 'start',
              align: positive ? 'start' : 'end',
              color: '#ffffff',
              formatter(value) {
                return parseFloat(positive ? value : (value * -1)).toFixed(2);
              },
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            xAxisID: 'topaxis'
          });
        }
        barchartData.datasets[dataIndex].data[labelIndex] = positive ? scoreRow.averageScore : (scoreRow.averageScore * -1);
        totals[labelIndex].dimensions += 1;
        if (totals[labelIndex].dimensions > 1) doTotals = true;
        totals[labelIndex].responses += parseInt(scoreRow.numberOfResponses, 10);
        totals[labelIndex].cumulativeScore += parseInt(scoreRow.numberOfResponses, 10) * parseFloat(positive ? scoreRow.averageScore : (scoreRow.averageScore * -1));
      });

      if (doTotals && !this.skipTotals) {
        barchartData.datasets.push({
          label: 'Overall',
          data: totals.map(total => (total.cumulativeScore / total.responses).toFixed(2)),
          datalabels: {
            anchor: positive ? 'end' : 'start',
            align: positive ? 'start' : 'end',
            display: true,
            color: '#000000',
            formatter(value) {
              return parseFloat(positive ? value : (value * -1)).toFixed(2);
            },
            font: {
              size: 12,
              weight: 'bold'
            }
          },
          xAxisID: 'topaxis'
        });
      }

      // set colors
      const totalDatasets = barchartData.datasets.length;
      barchartData.datasets.forEach((dataset, index) => {
        barchartData.datasets[index].backgroundColor = `rgba(${color[0] + (colorStep[0] / totalDatasets * index)}, ${color[1] + (colorStep[1] / totalDatasets * index)}, ${color[2] + (colorStep[2] / totalDatasets * index)}, 1)`;
      });

      this.renderChart(barchartData, {        
        responsive: true,
        maintainAspectRatio: true,
        title: {
          display: true,
          text: `${this.programmeName} ${positive ? 'Positive Elements' : 'Limiters'} (DO ${positive ? 'MORE' : 'LESS'})`,
          fontStyle: 'bold',
          fontSize: 24,
          fontColor: '#000000',
          fontFamily: /^CoR/.test(this.programmeName) ? "Calibri" : "Stag-Black"
        },
        legend: {
          display: true,
          position: 'left',
          align: 'start',
          labels: {
            fontSize: 16,
            fontStyle: 'bold'
          }
        },
        border: {
          display: true
        },
        layout: {
          padding: {
            right: 0
          }
        },
        scales: {
          xAxes: [{
            id: 'topaxis',
            position: 'top',
            offset: false,
            ticks: {
              display: true,
              fontColor: positive ? '#2E455F' : '#953735',
              fontStyle: 'bold',
              min: positive ? 1 : -4,
              max: positive ? 4 : -1,
              stepSize: 1,
              callback(label, index) {
                switch (index) {
                case 0:
                  return 'POOR';
                case 1:
                  return 'NOT SO GOOD';
                case 2:
                  return 'GOOD';
                case 3:
                  return 'VERY GOOD';
                default:
                  return '';
                }
              }
            },
            gridLines: {
              display: true,
              lineWidth: 1,
              drawBorder: true,
              drawOnChartArea: true,
              borderDashOffset: 15,
              color: '#000000',
              drawTicks: false
            }
          },
          {
            id: 'bottomaxis',
            position: 'bottom',
            offset: false,
            display: true,
            ticks: {
              display: false
            },
            gridLines: {
              display: true,
              lineWidth: 1,
              drawBorder: true,
              drawOnChartArea: false,
              borderDashOffset: 15,
              color: '#000000',
              drawTicks: false
            }
          }],
          yAxes: [{
            stacked: false,
            offset: true,
            position: positive ? 'left' : 'right',
            ticks: {
              display: true,
              fontSize: 16,
              fontStyle: 'bold'
            },
            gridLines: {
              display: false,
              drawBorder: true,
              drawOnChartArea: true,
              lineWidth: 2,
              zeroLineWidth: 1
              
            }
          }]
        },
        showTooltips: true,
        plugins: {
          datalabels: {
            display: true
          }
        }
      });
    }
  }
};



// WEBPACK FOOTER //
// ./src/graphs/ScoreBarChart.js